import React, { useState } from 'react';
import { Link } from 'react-router';
import {
  LEGAL_DOCUMENTS,
  currentLegalVersion,
  upcomingLegalVersion,
} from '@lds-efsp/shared-contracts';
import type { LegalDocumentId } from '@lds-efsp/shared-contracts';
import { MarkdownContent } from '../components/MarkdownContent';

interface LegalDocumentPageProps {
  /**
   * Resolved by the route declaration in App.tsx — one public route per
   * registry document id, no URL-param parsing in between.
   */
  documentId: LegalDocumentId;
}

/**
 * The one shared page for the public /legal/<id> routes. The registry is the
 * only source of the title and the body, and it reads no clock, so the page
 * supplies `now` itself; whatever `currentLegalVersion` returns for that
 * instant is what renders. Readable signed in or out — no auth guard.
 *
 * The label beside the version line and the announcement beneath it are both
 * functions of what the registry selectors answer for that same pinned instant.
 * Nothing here compares a version's own dates against a clock: a text starts
 * governing because `currentLegalVersion` starts answering it and stops being
 * announced because `upcomingLegalVersion` stops answering it, so both flip on
 * the effective instant with no deploy.
 */
const LegalDocumentPage: React.FC<LegalDocumentPageProps> = ({ documentId }) => {
  const document = LEGAL_DOCUMENTS.find((entry) => entry.id === documentId);
  // Pin the visit instant once per mount: the registry reads no clock, and one
  // visit should show one current version. The state initializer is the
  // render-sanctioned place to read the clock.
  const [now] = useState(() => new Date().toISOString());
  const version = currentLegalVersion(documentId, now);
  // The announced text, asked of the same registry at the same pinned instant.
  // Undefined is the ordinary answer — it is what the banner renders nothing
  // for, and what it returns to the moment the announced version takes effect.
  const upcoming = upcomingLegalVersion(documentId, now);

  if (document === undefined || version === undefined) {
    // No version is effective yet (a future-dated registry). Renderable rather
    // than a crash the day one is appended.
    return (
      <div className="mx-auto max-w-3xl px-4 py-8">
        <p className="text-gray-500">This document is not yet available.</p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl px-4 py-8">
      <h1 className="text-3xl font-bold text-gray-900">{document.title}</h1>
      {/* No version chrome for the informational document (R10): its real text
          takes effect at the instant it is posted, so it never has a version to
          name apart from the one it serves. The absence is the carve-out, not a
          missing render — the registry declares which acceptance model the id
          requires, and the page reads that declaration rather than the id, so a
          document that stops being informational gets its line back by
          declaration alone. */}
      {document.acceptanceModel !== 'informational' && (
        <>
          {/* The label sits on the version the registry answered above and
              nowhere else: it is its own element beside the line naming that
              version rather than part of it, so the version line text is
              unchanged. */}
          <p className="mt-1 text-sm font-medium text-green-700">In force</p>
          {/* The ISO date part is the UTC date — identical on every machine, no Intl. */}
          <p className="mt-1 text-sm text-gray-500">
            Version {version.version} · Effective {version.effectiveDate.slice(0, 10)}
          </p>
        </>
      )}
      {/* The announcement, keyed on the selector's answer alone — no model
          guard, because the announced question has no carve-out: a document
          that never announces is one the selector never answers for. It names
          only the version that answer carried, so it cannot outlive its own
          announcement. */}
      {upcoming !== undefined && (
        <div className="mt-4 rounded-md border border-blue-200 bg-blue-50 p-4 text-sm text-blue-900">
          A new version takes effect on {upcoming.effectiveDate.slice(0, 10)}.{' '}
          <Link to={`/legal/${documentId}/upcoming`} className="font-medium underline">
            Read the upcoming version
          </Link>
        </div>
      )}
      <div className="mt-6">
        <MarkdownContent markdown={version.body} />
      </div>
    </div>
  );
};

export default LegalDocumentPage;
